import apiClient from './api';
import { searchSignupLocations, type SignupLocationRow } from './signupLocations';
import type { MonitoringArea } from '../types/auth';

/**
 * LGU assigned monitoring area from Digisaka FastAPI
 * ``GET /api/webgis/monitoring-area`` (proxies digisaka.app).
 */
export type MonitoringAreaBoundaryFeature = GeoJSON.Feature<
  GeoJSON.Polygon | GeoJSON.MultiPolygon,
  {
    label?: string;
    level?: string;
    province?: string;
    municipality?: string;
    barangay?: string;
    layer_role?: string;
  }
>;

/** GeoJSON outline of the LGU monitoring area for map display. */
export async function fetchLguMonitoringAreaBoundary(): Promise<MonitoringAreaBoundaryFeature | null> {
  const { data } = await apiClient.get<{
    success?: boolean;
    data?: MonitoringAreaBoundaryFeature | null;
    message?: string;
  }>('/webgis/monitoring-area/boundary');
  const feature = data?.data;
  return feature && typeof feature === 'object' && feature.type === 'Feature' ? feature : null;
}

export async function fetchLguMonitoringArea(): Promise<MonitoringArea | null> {
  const { data } = await apiClient.get<{
    success?: boolean;
    data?: MonitoringArea | null;
    message?: string;
  }>('/webgis/monitoring-area');
  const area = data?.data;
  return area && typeof area === 'object' ? area : null;
}

function rowsFromPaged(kind: 'provinces' | 'municipalities' | 'barangays', opts: {
  province_id?: string;
  city_id?: string;
}): Promise<SignupLocationRow[]> {
  return searchSignupLocations(kind, { per_page: 100, ...opts });
}

function findById(rows: SignupLocationRow[], id: string): SignupLocationRow | undefined {
  return rows.find((r) => String(r.id) === id || String(r.code ?? '') === id);
}

/** Resolve human-readable names for monitoring-area codes via public location lookups. */
export async function enrichMonitoringAreaLabels(
  area: MonitoringArea
): Promise<MonitoringArea> {
  const out: MonitoringArea = { ...area };
  const provinceId = area.province_id != null ? String(area.province_id).trim() : '';
  const cityId = area.city_id != null ? String(area.city_id).trim() : '';
  const barangayId = area.barangay_id != null ? String(area.barangay_id).trim() : '';

  try {
    if (provinceId && !String(out.province || '').trim()) {
      // 88 provinces — one page of 100 is enough; also try direct search on id as fallback.
      let match = findById(await rowsFromPaged('provinces', {}), provinceId);
      if (!match) {
        match = findById(
          await searchSignupLocations('provinces', { search: provinceId, per_page: 20 }),
          provinceId
        );
      }
      if (match?.name) out.province = match.name;
    }

    if (cityId && !String(out.municipality || '').trim()) {
      let match = findById(
        await rowsFromPaged('municipalities', { province_id: provinceId || undefined }),
        cityId
      );
      if (!match) {
        // Paginate a few pages if province has many municipalities
        for (let page = 2; page <= 5 && !match; page++) {
          const more = await searchSignupLocations('municipalities', {
            page,
            per_page: 100,
            province_id: provinceId || undefined,
          });
          if (!more.length) break;
          match = findById(more, cityId);
        }
      }
      if (!match) {
        match = findById(
          await searchSignupLocations('municipalities', { search: cityId, per_page: 20 }),
          cityId
        );
      }
      if (match?.name) out.municipality = match.name;
    }

    if (barangayId && !String(out.barangay || '').trim()) {
      let match = findById(
        await rowsFromPaged('barangays', { city_id: cityId || undefined }),
        barangayId
      );
      if (!match) {
        for (let page = 2; page <= 8 && !match; page++) {
          const more = await searchSignupLocations('barangays', {
            page,
            per_page: 100,
            city_id: cityId || undefined,
          });
          if (!more.length) break;
          match = findById(more, barangayId);
        }
      }
      if (match?.name) out.barangay = match.name;
    }
  } catch {
    // Labels are best-effort; IDs still display as fallback.
  }
  return out;
}

/** Prefer display name for sidebar / badges (never bare id when a name exists). */
export function formatMonitoringAreaLabel(area: MonitoringArea | null | undefined): string | null {
  if (!area || typeof area !== 'object') return null;
  const level = String(area.level || '').toLowerCase();
  if (level === 'barangay') {
    const name = String(area.barangay || '').trim();
    return name || null;
  }
  if (level === 'municipality') {
    const name = String(area.municipality || '').trim();
    return name || null;
  }
  if (level === 'province') {
    const name = String(area.province || '').trim();
    return name || null;
  }
  return (
    String(area.barangay || area.municipality || area.province || '').trim() || null
  );
}

/** Attach resolved names onto a User.monitoring_area when missing. */
export async function withEnrichedMonitoringArea<T extends { role?: string; monitoring_area?: MonitoringArea | null }>(
  user: T
): Promise<T> {
  if (String(user.role || '').toLowerCase() !== 'lgu') return user;
  let area = user.monitoring_area;
  if (!area || typeof area !== 'object') {
    try {
      area = await fetchLguMonitoringArea();
    } catch {
      return user;
    }
  }
  if (!area) return { ...user, monitoring_area: null };
  const enriched = await enrichMonitoringAreaLabels(area);
  return { ...user, monitoring_area: enriched };
}
